import {
  type BindWebAuthnPayload,
  InteractionEvent,
  type SignInIdentifier,
  type WebAuthnAuthenticationOptions,
  type WebAuthnVerificationPayload,
} from '@logto/schemas';

import api from '../api';

import { experienceApiRoutes } from './const';
import {
  identifyAndSubmitInteraction,
  identifyUser,
  initInteraction,
  submitInteraction,
} from './interaction';

export { createWebAuthnRegistration as createSignInPasskeyRegistrationOptions } from './mfa';

export const bindSignInPasskey = async (payload: BindWebAuthnPayload, verificationId: string) => {
  await api.post(`${experienceApiRoutes.verification}/web-authn/registration/verify`, {
    json: {
      verificationId,
      payload,
    },
  });
  await api.post(`${experienceApiRoutes.profile}/mfa/passkey`, {
    json: {
      verificationId,
    },
  });
  return submitInteraction();
};

export const createSignInPasskeyAuthenticationOptions = async () =>
  api.post(`${experienceApiRoutes.prefix}/preflight/sign-in-passkey/authentication`).json<{
    authenticationOptions: WebAuthnAuthenticationOptions;
  }>();

export const verifySignInPasskey = async (payload: WebAuthnVerificationPayload) => {
  const { verificationId } = await api
    .post(`${experienceApiRoutes.verification}/sign-in-passkey/authentication/verify`, {
      json: { payload },
    })
    .json<{ verificationId: string }>();

  return identifyAndSubmitInteraction({ verificationId });
};

/**
 * Skip binding passkey for the current user.
 *
 * Note: When calling this API on sign-up, the skip is temporary for the current interaction.
 * On sign-in, the skip flag will be persisted to user config.
 */
export const skipPasskeyBinding = async () => {
  await api.post(`${experienceApiRoutes.profile}/mfa/passkey-skipped`);
  return submitInteraction();
};

/**
 * Create WebAuthn authentication options for identifier-based passkey sign-in.
 * The user has already provided an identifier (email/phone/username) and we
 * generate non-discoverable authentication options for their passkey credentials.
 */
export const createIdentifierPasskeyAuthentication = async (identifier: {
  type: SignInIdentifier;
  value: string;
}) => {
  await initInteraction(InteractionEvent.SignIn);

  const { verificationId, authenticationOptions } = await api
    .post(`${experienceApiRoutes.verification}/sign-in-passkey/authentication`, {
      json: { identifier },
    })
    .json<{
      verificationId: string;
      authenticationOptions: WebAuthnAuthenticationOptions;
    }>();

  return { verificationId, options: authenticationOptions };
};

/**
 * Verify the identifier-based passkey authentication response
 * and identify + submit the interaction.
 */
export const verifyIdentifierPasskey = async (
  verificationId: string,
  payload: WebAuthnVerificationPayload
) => {
  const result = await api
    .post(`${experienceApiRoutes.verification}/sign-in-passkey/authentication/verify`, {
      json: { verificationId, payload },
    })
    .json<{ verificationId: string }>();

  return identifyAndSubmitInteraction({ verificationId: result.verificationId });
};

/**
 * Start a password-recovery interaction backed by a discoverable passkey.
 * The interaction is deliberately initialized as ForgotPassword so a successful
 * credential assertion can only be used to set a new password, not to mint a
 * sign-in session directly.
 */
export const createForgotPasswordPasskeyAuthentication = async () => {
  await initInteraction(InteractionEvent.ForgotPassword);

  const { authenticationOptions } = await api
    .post(`${experienceApiRoutes.prefix}/preflight/sign-in-passkey/authentication`)
    .json<{ authenticationOptions: WebAuthnAuthenticationOptions }>();

  return { options: authenticationOptions };
};

/**
 * Verify a passkey for password recovery and identify the account. Do not
 * submit the interaction here: the reset-password page must first provide and
 * validate the replacement password.
 */
export const verifyForgotPasswordPasskey = async (payload: WebAuthnVerificationPayload) => {
  const result = await api
    .post(`${experienceApiRoutes.verification}/sign-in-passkey/authentication/verify`, {
      json: { payload },
    })
    .json<{ verificationId: string }>();

  await identifyUser({ verificationId: result.verificationId });
};
